import { combineReducers } from 'redux';
import { call, debounce, put, select, takeLatest, takeLeading } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { DocsApprovingData, DocumentInfo, DocumentListResponse } from 'generated/bffDocument/models';
import { ExternalUserInfo, ExternalUsersResponse } from 'generated/bffUser/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import dayjs from 'helpers/dayjs';
import { downloadUrl } from 'helpers/downloadHandler';
import {
  AppAction,
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createLoadingStateReducer,
  createReducer,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { CustomerInfo } from 'models/context/CustomerInfo';
import { DocumentsFilterParams, GetDocumentDetailPayload } from 'models/Document';
import { GetDocumentListForConfirmationRequest } from 'models/GetDocumentListForConfirmationRequest';
import { LegacyCrmDeeplinkCommand } from 'models/LegacyCrmDeeplink';
import { Pagination } from 'models/Pagination';

import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { selectAuthUserType } from 'saga/auth/ducks';
import { selectContextCustomerInfo } from 'saga/context/customer/ducks';
import { selectContextInternalIsUserScreenFilterDisabled } from 'saga/context/internal/ducks';
import { redirectToLegacyCrmAction } from 'saga/crm-deeplinks/ducks';
import { getKahlActions } from 'saga/kahl/ducks';
import { selectProperty } from 'saga/property/ducks';

/* CONSTANTS */
const debounceTime = 500;

/* STATE */
export interface DocumentState {
  data: DocumentInfo[];
  loadingStatus: LoadingStatus;
  documentDetail: DocumentInfo | null;
  totalCount: number;
  filter: DocumentsFilterParams;
  pagination: Pagination;
  externalUsers: ExternalUserInfo[];
  totalItemsFound: number;
  documentListForConfirmation: DocsApprovingData[];
  financialDocumentListForSending: DocumentInfo[];
}

/* ACTION TYPES */
export enum DocumentActionTypes {
  SetFilter = '@@Document/SET_FILTER',
  ResetFilter = '@@Document/RESET_FILTER',
  SetLoading = '@@Document/SET_LOADING',
  SetFilterDebounced = '@@Document/SET_FILTER_DEBOUNCED',
  SetPage = '@@Document/SET_PAGE',
  GetDocumentList = '@@Document/GET_DOCUMENT_LIST',
  GetDocumentDetail = '@@Document/GET_DOCUMENT_DETAIL',
  DeleteIndex = '@@Document/DELETE_INDEX',
  DownloadDocuments = '@@Document/DOWNLOAD_DOCUMENT',
  AddIndex = '@@Document/ADD_INDEX',
  GetExternalUsers = '@@Document/GET_EXTERNAL_USERS',
  GetExternalUsersDebounced = '@@Document/GET_EXTERNAL_USERS_DEBOUNCED',
  GetDocumentListForConfirmation = '@@Document/GET_DOCUMENT_LIST_FOR_CONFIRMATION',
  PutDocumentListForConfirmation = '@@Document/PUT_DOCUMENT_LIST_FOR_CONFIRMATION',
  GetDocumentForConfirmationDownloadInfo = '@@Document/GET_DOCUMENT_LIST_FOR_CONFIRMATION_DOWNLOAD',
  GetFinancialDocumentForSending = '@@Document/GET_FINANCIAL_DOCUMENT_LIST_FOR_SENDING',
}

/* ACTIONS */
export const setFilterAction = createActionCreator(DocumentActionTypes.SetFilter);
export const setLoadingAction = createActionCreator(DocumentActionTypes.SetLoading);
export const setFilterDebouncedAction = createActionCreator(DocumentActionTypes.SetFilterDebounced);
export const setPageAction = createActionCreator(DocumentActionTypes.SetPage);
export const getDocumentListActions = createApiActionCreators(DocumentActionTypes.GetDocumentList);
export const getDocumentDetailActions = createApiActionCreators(DocumentActionTypes.GetDocumentDetail);
export const deleteIndexActions = createApiActionCreators(DocumentActionTypes.DeleteIndex);
export const updateIndexActions = createApiActionCreators(DocumentActionTypes.AddIndex);
export const downloadDocumentsActions = createApiActionCreators(DocumentActionTypes.DownloadDocuments);
export const getExternalUsersActions = createApiActionCreators(DocumentActionTypes.GetExternalUsers);
export const getExternalUsersDebouncedAction = createActionCreator(DocumentActionTypes.GetExternalUsersDebounced);
export const resetFilterAction = createActionCreator(DocumentActionTypes.ResetFilter);
export const getDocumentListForConfirmationAction = createApiActionCreators(
  DocumentActionTypes.GetDocumentListForConfirmation
);
export const putDocumentListForConfirmationAction = createApiActionCreators(
  DocumentActionTypes.PutDocumentListForConfirmation
);
export const getDocumentForConfirmationDownloadInfoAction = createApiActionCreators(
  DocumentActionTypes.GetDocumentForConfirmationDownloadInfo
);
export const getFinancialDocumentForSendingAction = createApiActionCreators(
  DocumentActionTypes.GetFinancialDocumentForSending
);

/* REDUCERS */
const initialState: DocumentState = {
  data: [],
  loadingStatus: LoadingStatus.loading,
  documentDetail: null,
  totalCount: 0,
  filter: {
    dateTo: dayjs().format('YYYY-MM-DD'),
    dateFrom: dayjs().subtract(14, 'days').format('YYYY-MM-DD'),
  },
  pagination: {
    page: 1,
    pageSize: 0,
    forceCheck: false,
    initPages: 1,
  },
  externalUsers: [],
  totalItemsFound: 0,
  documentListForConfirmation: [],
  financialDocumentListForSending: [],
};

const data = createReducer(initialState.data, {
  [DocumentActionTypes.GetDocumentList]: {
    [RequestActionTypes.SUCCESS]: (state: DocumentInfo[], payload: DocumentListResponse & Pagination) => {
      if (payload.page > 1) {
        return [...state, ...(payload.documentList || [])];
      }

      return payload.documentList || [];
    },
    [RequestActionTypes.FAILURE]: () => [],
  },
});

const loadingStatus = createLoadingStateReducer(
  initialState.loadingStatus,
  {
    [DocumentActionTypes.GetDocumentList]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
    [DocumentActionTypes.GetFinancialDocumentForSending]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
  },
  {
    [DocumentActionTypes.SetLoading]: (state: LoadingStatus, status: LoadingStatus) => status,
  }
);

const totalCount = createReducer(initialState.totalCount, {
  [DocumentActionTypes.GetDocumentList]: {
    [RequestActionTypes.SUCCESS]: (_state: number, payload: DocumentListResponse & Pagination) =>
      (payload.pageCount || 0) * payload.pageSize,
    [RequestActionTypes.FAILURE]: () => 0,
  },
});

const filter = createReducer(initialState.filter, {
  [DocumentActionTypes.SetFilter]: (state: DocumentsFilterParams, payload: Partial<DocumentsFilterParams>) => ({
    ...state,
    ...payload,
  }),
  [DocumentActionTypes.ResetFilter]: () => ({}),
  [DocumentActionTypes.SetFilterDebounced]: (
    state: DocumentsFilterParams,
    payload: Partial<DocumentsFilterParams>
  ) => ({
    ...state,
    ...payload,
  }),
});

const pagination = createReducer(initialState.pagination, {
  [DocumentActionTypes.SetPage]: (_state: Pagination, payload: Pagination) => payload,
  [DocumentActionTypes.SetFilter]: (state: Pagination) => ({
    ...state,
    page: initialState.pagination.page,
  }),
  [DocumentActionTypes.SetFilterDebounced]: (state: Pagination) => ({
    ...state,
    page: initialState.pagination.page,
  }),
  [DocumentActionTypes.GetDocumentList]: {
    [RequestActionTypes.SUCCESS]: (state: Pagination) => ({
      ...state,
      page: state.page < state.initPages ? state.initPages : state.page,
    }),
  },
});

const documentDetail = createReducer(initialState.documentDetail, {
  [DocumentActionTypes.GetDocumentDetail]: {
    [RequestActionTypes.REQUEST]: (state: DocumentInfo | null, payload: GetDocumentDetailPayload) => {
      if (state?.documentGUID === payload.documentGUID && state?.objectStoreName === payload.objectStoreName) {
        return state;
      }

      return null;
    },
    [RequestActionTypes.SUCCESS]: (state: DocumentInfo | null, payload: DocumentInfo) => payload,
    [RequestActionTypes.FAILURE]: () => null,
  },
});

const externalUsers = createReducer(initialState.externalUsers, {
  [DocumentActionTypes.GetExternalUsers]: {
    [RequestActionTypes.SUCCESS]: (state: ExternalUserInfo[], payload: ExternalUsersResponse & { page: number }) => {
      if (payload.page > 1) {
        return [...state, ...(payload.externalUserList || [])];
      }

      return payload.externalUserList || [];
    },
    [RequestActionTypes.FAILURE]: () => [],
  },
});

const totalItemsFound = createReducer(initialState.totalItemsFound, {
  [DocumentActionTypes.GetExternalUsers]: {
    [RequestActionTypes.SUCCESS]: (state: number, payload: ExternalUsersResponse) => payload?.totalItemsFound || 0,
    [RequestActionTypes.FAILURE]: () => 0,
  },
});

const documentListForConfirmation = createReducer(initialState.documentListForConfirmation, {
  [DocumentActionTypes.GetDocumentListForConfirmation]: {
    [RequestActionTypes.SUCCESS]: (state: Array<any> | null, payload: Array<any>) => payload,
    [RequestActionTypes.FAILURE]: () => [],
  },
});

const financialDocumentListForSending = createReducer(initialState.financialDocumentListForSending, {
  [DocumentActionTypes.GetFinancialDocumentForSending]: {
    [RequestActionTypes.SUCCESS]: (state: DocumentInfo[], payload: DocumentListResponse) => payload.documentList || [],
    [RequestActionTypes.FAILURE]: () => [],
  },
});

export default combineReducers<DocumentState>({
  data,
  loadingStatus,
  documentDetail,
  totalCount,
  filter,
  pagination,
  externalUsers,
  totalItemsFound,
  documentListForConfirmation,
  financialDocumentListForSending,
});

/* SELECTORS*/
const selectDocument = (state: AppState) => state.document;

export const selectDocumentLoading = (state: AppState): LoadingStatus => selectDocument(state).loadingStatus;

export const selectDocumentFilter = (state: AppState): DocumentsFilterParams => selectDocument(state).filter;

export const selectDocumentPagination = (state: AppState): Pagination => selectDocument(state).pagination;

export const selectDocumentData = (state: AppState): DocumentInfo[] => {
  const isInternal = selectContextInternalIsUserScreenFilterDisabled(state);

  return selectDocument(state)
    .data.filter((item) => item.extVisible || isInternal)
    .sort((a, b) => new Date(b.documentDateCreated || 0).getTime() - new Date(a.documentDateCreated || 0).getTime());
};

export const selectDocumentTotalCount = (state: AppState): number => selectDocument(state).totalCount;

export const selectDocumentDetailData = (state: AppState): DocumentInfo | null => selectDocument(state).documentDetail;

export const selectDocumentExternalUsers = (state: AppState): ExternalUserInfo[] | [] =>
  selectDocument(state).externalUsers;

export const selectTotalItemsFound = (state: AppState): number => selectDocument(state).totalItemsFound;

export const selectDocumentListForConfirmation = (state: AppState): DocsApprovingData[] | [] =>
  selectDocument(state).documentListForConfirmation;

export const selectFinancialDocumentListForSending = (state: AppState): DocumentInfo[] =>
  selectDocument(state).financialDocumentListForSending;

/* SAGAS */
function* getDocumentDefaultPagination() {
  const pageSize = Number(selectProperty('gui.documentList.pageSize'));
  const initPages = Number(selectProperty('gui.documentList.loadPagesCount.Init'));

  const pagination: Pagination = yield select(selectDocumentPagination);

  yield put(
    setPageAction({
      ...pagination,
      initPages,
      pageSize: pageSize || 50,
    })
  );
}

function* getDocumentList() {
  yield put(setLoadingAction(LoadingStatus.loading));

  const filter: DocumentsFilterParams = yield select(selectDocumentFilter);
  const pagination: Pagination = yield select(selectDocumentPagination);

  if (!pagination.pageSize) {
    yield call(getDocumentDefaultPagination);

    return;
  }

  const paginationParam = {
    ...pagination,
    page: pagination.page < pagination.initPages ? 1 : pagination.page,
    pageSize: pagination.page < pagination.initPages ? pagination.pageSize * pagination.initPages : pagination.pageSize,
  };

  const dateFromValue: Date | null = filter.dateFrom ? new Date(filter.dateFrom) : null;
  const dateToValue: Date | null = filter.dateTo ? new Date(filter.dateTo) : null;
  const dateHasRangeValidOrIsUnset: boolean = !dateFromValue || !dateToValue || dateFromValue <= dateToValue;

  if (dateHasRangeValidOrIsUnset) {
    const request: ApiClientRequest = {
      apiCall: call(api.getDocumentList, yield getApiUrl(environment.urlConfig.documents), filter, paginationParam),
    };
    const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

    if (resp.ok) {
      yield put(
        getDocumentListActions.success({
          ...resp.data,
          ...paginationParam,
        })
      );
    } else {
      yield put(getDocumentListActions.failure());
    }
  }
}

function* getDocumentDetail({ payload }: any) {
  const request: ApiClientRequest = {
    apiCall: call(
      api.getDocumentDetail,
      yield getApiUrl(environment.urlConfig.documents),
      payload.documentGUID,
      payload.objectStoreName,
      payload.forceCheck
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    if (resp.data.documentList && resp.data.documentList.length) {
      yield put(getDocumentDetailActions.success(resp.data.documentList[0]));
    } else {
      yield put(getDocumentDetailActions.failure());
    }
  } else {
    yield put(getDocumentDetailActions.failure());
  }
}

function* getDocumentExternalUsers({ payload }: any) {
  const pageSize = Number(selectProperty('gui.entityManagement.searching_contactPerson.pageSize'));
  const guiScreenId: string = selectProperty('gui.entityManagement.searching_contactPerson.guiScreenId');
  const customer: CustomerInfo = yield select(selectContextCustomerInfo);

  const request: ApiClientRequest = {
    apiCall: call(
      api.getExternalUsers,
      yield getApiUrl(environment.urlConfig.user),
      customer?.cuRefNo,
      payload.search,
      guiScreenId,
      pageSize,
      payload.page || 1
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(
      getExternalUsersActions.success({
        ...resp.data,
        page: payload.page || 1,
      })
    );
  } else {
    yield put(getExternalUsersActions.failure());
  }
}

function* deleteIndex({ payload }: any) {
  const request: ApiClientRequest = {
    apiCall: call(
      api.deleteIndex,
      yield getApiUrl(environment.urlConfig.documents),
      payload.documentGUID,
      payload.objectStoreName,
      payload.propertyValue,
      payload.propertyName
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(deleteIndexActions.success());
    yield put(
      getDocumentDetailActions.request({
        documentGUID: payload.documentGUID,
        objectStoreName: payload.objectStoreName,
        forceCheck: true,
      })
    );
  } else {
    yield put(deleteIndexActions.failure());
  }
}

function* updateIndex({ payload }: any) {
  const request: ApiClientRequest = {
    apiCall: call(
      api.updateIndex,
      yield getApiUrl(environment.urlConfig.documents),
      payload.documentGUID,
      payload.objectStoreName,
      payload.propertyValue,
      payload.propertyName
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(updateIndexActions.success());
    yield put(
      getDocumentDetailActions.request({
        documentGUID: payload.documentGUID,
        objectStoreName: payload.objectStoreName,
        forceCheck: true,
      })
    );
  } else {
    yield put(updateIndexActions.failure());
  }
}

function* downloadDocument({ payload }: any) {
  const request: ApiClientRequest = {
    apiCall: call(
      api.downloadDocument,
      yield getApiUrl(environment.urlConfig.documents),
      payload.documentGUID,
      payload.objectStoreName
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    const doc = resp.data.documentList && resp.data.documentList[0];

    downloadUrl(doc?.downloadUrl);
  } else {
    yield put(downloadDocumentsActions.failure());
  }
}

function* getDocumentListForConfirmation({ payload }: AppAction<GetDocumentListForConfirmationRequest>) {
  const hideKAHLMessages: boolean | undefined = payload.hideKAHLMessages;
  const request: ApiClientRequest = {
    apiCall: call(
      api.getDocumentListForConfirmation,
      yield getApiUrl(environment.urlConfig.documents),
      payload.cuRefNo,
      payload.cpRefNo
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    if (resp.data) {
      yield put(getDocumentListForConfirmationAction.success(resp.data));

      if (resp.data.length === 0 && !hideKAHLMessages) {
        yield put(getKahlActions.request({ infoMessageIds: 'ID1581' }));
      }
    } else {
      if (!hideKAHLMessages) {
        yield put(getKahlActions.request({ infoMessageIds: 'ID1581' }));
      }

      yield put(getDocumentListForConfirmationAction.failure());
    }
  } else {
    if (!hideKAHLMessages) {
      yield put(getKahlActions.request({ infoMessageIds: 'ID0992' }));
    }

    yield put(getDocumentListForConfirmationAction.failure());
  }
}

function* putDocumentListForConfirmation({ payload }: any) {
  const request: ApiClientRequest = {
    apiCall: call(
      api.putDocumentListForConfirmation,
      yield getApiUrl(environment.urlConfig.documents),
      payload.docsApprovingEntityList,
      payload.cuRefNo,
      payload.cpRefNo
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    const authUserType: string = yield select(selectAuthUserType);

    yield put(
      redirectToLegacyCrmAction({
        command: authUserType === 'internal' ? LegacyCrmDeeplinkCommand.IMO2DASH : LegacyCrmDeeplinkCommand.EMO2DASH,
        queryParams: {
          cuRefNo: payload.cuRefNo,
          // eslint-disable-next-line camelcase
          error_flag: 'ID1576',
        },
      })
    );
  }
}

function* getDocumentForConfirmationDownloadInfo({ payload }: any) {
  const request: ApiClientRequest = {
    apiCall: call(
      api.getDocumentForConfirmationDownloadInfo,
      yield getApiUrl(environment.urlConfig.documents),
      payload.jobId,
      payload.cuRefNo,
      payload.cpRefNo
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    if (resp.data) {
      window.open(resp.data.downloadUrl, '_blank');
    }
  }
}

function* getFinancialDocumentForSending({ payload }: AppAction<DocumentsFilterParams>) {
  const request: ApiClientRequest = {
    apiCall: call(api.getFinancialDocumentForSending, yield getApiUrl(environment.urlConfig.documents), payload),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getFinancialDocumentForSendingAction.success(resp.data));
  } else {
    yield put(getFinancialDocumentForSendingAction.failure());
  }
}

/* EXPORT */
export function* documentSaga() {
  yield takeLatest(
    [
      createActionType(DocumentActionTypes.GetDocumentList, RequestActionTypes.REQUEST),
      DocumentActionTypes.SetFilter,
      DocumentActionTypes.SetPage,
    ],
    getDocumentList
  );

  yield debounce(debounceTime, DocumentActionTypes.SetFilterDebounced, getDocumentList);

  yield takeLatest(
    createActionType(DocumentActionTypes.GetDocumentDetail, RequestActionTypes.REQUEST),
    getDocumentDetail
  );

  yield takeLatest(createActionType(DocumentActionTypes.DeleteIndex, RequestActionTypes.REQUEST), deleteIndex);

  yield takeLatest(
    createActionType(DocumentActionTypes.GetDocumentListForConfirmation, RequestActionTypes.REQUEST),
    getDocumentListForConfirmation
  );

  yield takeLatest(createActionType(DocumentActionTypes.AddIndex, RequestActionTypes.REQUEST), updateIndex);

  yield takeLatest(
    createActionType(DocumentActionTypes.DownloadDocuments, RequestActionTypes.REQUEST),
    downloadDocument
  );

  yield takeLatest(
    createActionType(DocumentActionTypes.GetExternalUsers, RequestActionTypes.REQUEST),
    getDocumentExternalUsers
  );

  yield takeLeading(
    createActionType(DocumentActionTypes.PutDocumentListForConfirmation, RequestActionTypes.REQUEST),
    putDocumentListForConfirmation
  );

  yield takeLatest(
    createActionType(DocumentActionTypes.GetDocumentForConfirmationDownloadInfo, RequestActionTypes.REQUEST),
    getDocumentForConfirmationDownloadInfo
  );

  yield takeLatest(
    createActionType(DocumentActionTypes.GetFinancialDocumentForSending, RequestActionTypes.REQUEST),
    getFinancialDocumentForSending
  );

  yield debounce(debounceTime, DocumentActionTypes.GetExternalUsersDebounced, getDocumentExternalUsers);
}
